import React, { useEffect, useState } from "react";
import {
  FaCut,
  FaDrawPolygon,
  FaInfoCircle,
  FaMapMarkedAlt,
  FaRulerCombined,
  FaSeedling,
  FaTimes,
} from "react-icons/fa";

import {
  buildPlotName,
  formatAreaHa,
  formatDistanceM,
} from "../../utils/mapMeasurements";
import FarmMapFloatingPanel from "./FarmMapFloatingPanel";

import "./FarmMapDrawingPanel.css";

const INSTRUCTION_PANEL_LAYOUT = { x: 12, y: 52, width: 360, height: 170 };
const MEASURE_DISTANCE_PANEL_LAYOUT = { x: 12, y: 52, width: 300, height: 190 };
const RESULT_PANEL_LAYOUT = { x: 12, y: 56, width: 380, height: 300 };
const FARM_RESULT_PANEL_LAYOUT = { x: 12, y: 56, width: 380, height: 340 };

function FarmMapDrawingPanel({
  t,
  farmId,
  activeTool,
  measureOnly,
  pendingShape,
  isAddingHole,
  savingDrawing,
  toolsReady,
  draftPointCount,
  draftLineDistanceM,
  draftLastSegmentM,
  minDraftPoints,
  editingCropId,
  editingSubFarmId,
  onStartTool,
  onCloseTool,
  onClearPending,
  onClearMeasureDraft,
  onFinishDraft,
  onUndoDraftPoint,
  onStartAddingHole,
  onCancelAddingHole,
  onRemoveHole,
  onSaveCrop,
  onSaveFarm,
}) {
  const [cropName, setCropName] = useState("");
  const [farmName, setFarmName] = useState("");
  const [showFarmHelp, setShowFarmHelp] = useState(false);
  const isEditingCrop = Boolean(editingCropId);
  const isEditingSubFarm = Boolean(editingSubFarmId);
  const isUpdatingCrop = Boolean(
    pendingShape && pendingShape.type === "polygon" && pendingShape.editCropId
  );
  const isUpdatingFarm = Boolean(
    pendingShape && pendingShape.type === "farm" && pendingShape.editSubFarmId
  );
  const holeCount = pendingShape && pendingShape.holes ? pendingShape.holes.length : 0;
  const isLiveDistanceMeasure = activeTool === "line" && measureOnly;
  const showInstruction =
    ((activeTool && !pendingShape) || (pendingShape && isAddingHole)) &&
    !isLiveDistanceMeasure;

  useEffect(() => {
    if (!pendingShape || pendingShape.type !== "polygon") {
      setCropName("");
      return;
    }
    if (pendingShape.editCropId && pendingShape.cropName) {
      setCropName(pendingShape.cropName);
    } else {
      setCropName("");
    }
  }, [pendingShape]);

  useEffect(() => {
    if (!pendingShape || pendingShape.type !== "farm") {
      setFarmName("");
      return;
    }
    if (pendingShape.editSubFarmId && pendingShape.subFarmName) {
      setFarmName(pendingShape.subFarmName);
    } else if (!pendingShape.editSubFarmId) {
      setFarmName("");
    }
  }, [pendingShape]);

  const resolvedCropName =
    cropName.trim() ||
    (pendingShape && pendingShape.cropName ? pendingShape.cropName.trim() : "");
  const canSaveCrop = Boolean(resolvedCropName) || isUpdatingCrop;

  function handleSaveCropClick() {
    onSaveCrop(resolvedCropName);
    setCropName("");
  }

  function handleSaveFarmClick() {
    onSaveFarm(farmName.trim());
    setFarmName("");
  }

  const plotPreviewName =
    pendingShape && pendingShape.type === "polygon"
      ? buildPlotName(cropName, pendingShape.areaHa)
      : "";

  function instructionText() {
    if (isAddingHole) {
      return t(
        "Desenhe o recorte interno que deve ser subtraído da área. Use Concluir quando terminar."
      );
    }
    if (isEditingSubFarm) {
      return t(
        "Arraste os pontos numerados para ajustar o perímetro da fazenda. Clique no mapa para adicionar vértices. Use Concluir quando terminar."
      );
    }
    if (activeTool === "line") {
      return t(
        "Clique no mapa para marcar os pontos. A distância total é atualizada automaticamente."
      );
    }
    if (activeTool === "farm") {
      return t(
        "Desenhe o contorno da fazenda (somente a linha externa). Talhões dentro do perímetro serão vinculados automaticamente."
      );
    }
    if (isEditingCrop) {
      return t(
        "Arraste os pontos numerados para corrigir as bordas. Clique no mapa para adicionar vértices. Use Concluir quando terminar."
      );
    }
    return t(
      "Clique no mapa para marcar os vértices do talhão. Use Concluir quando terminar."
    );
  }

  return (
    <>
      <div className="farm-map-drawing-toolbar">
        <button
          type="button"
          className={`btn btn-default btn-sm ${
            activeTool === "line" && measureOnly ? "is-active" : ""
          }`}
          onClick={() => onStartTool("line", true)}
          disabled={!toolsReady || isEditingCrop || isEditingSubFarm}
          title={t("Medir distância")}
        >
          <FaRulerCombined style={{ marginRight: 6 }} />
          {t("Medir distância")}
        </button>
        <button
          type="button"
          className={`btn btn-default btn-sm ${
            activeTool === "polygon" && !measureOnly ? "is-active" : ""
          }`}
          onClick={() => onStartTool("polygon", false)}
          disabled={!toolsReady || isEditingCrop || isEditingSubFarm}
          title={t("Desenhar talhões, medir e salvar cultura")}
        >
          <FaMapMarkedAlt style={{ marginRight: 6 }} />
          {t("Desenhar Talhões")}
        </button>
        <button
          type="button"
          className={`btn btn-default btn-sm ${
            activeTool === "polygon" && measureOnly ? "is-active" : ""
          }`}
          onClick={() => onStartTool("polygon", true)}
          disabled={!toolsReady || isEditingCrop || isEditingSubFarm}
          title={t("Medir área e perímetro")}
        >
          <FaSeedling style={{ marginRight: 6 }} />
          {t("Medir área")}
        </button>
        <span className="farm-map-drawing-toolbar__farm-group">
          <button
            type="button"
            className={`btn btn-default btn-sm ${
              activeTool === "farm" ? "is-active" : ""
            }`}
            onClick={() => onStartTool("farm", false)}
            disabled={!toolsReady || isEditingCrop || isEditingSubFarm}
            title={t("Desenhar perímetro da fazenda")}
          >
            <FaDrawPolygon style={{ marginRight: 6 }} />
            {t("Desenhar fazenda")}
          </button>
          <button
            type="button"
            className="farm-map-drawing-toolbar__info"
            title={t("Como funciona o perímetro da fazenda?")}
            aria-label={t("Como funciona o perímetro da fazenda?")}
            onClick={() => setShowFarmHelp((current) => !current)}
          >
            <FaInfoCircle />
          </button>
        </span>
        {activeTool || pendingShape || isAddingHole ? (
          <button
            type="button"
            className="btn btn-default btn-sm farm-map-drawing-toolbar__close"
            onClick={onCloseTool}
          >
            <FaTimes style={{ marginRight: 6 }} />
            {t("Fechar")}
          </button>
        ) : null}
      </div>

      {showFarmHelp ? (
        <div className="farm-map-drawing-farm-help">
          <p>
            {t(
              "Desenhe apenas o contorno externo da fazenda. Talhões cujo centro estiver dentro do perímetro serão vinculados automaticamente a ela — a ordem de criação (fazenda ou talhão) não importa."
            )}
          </p>
        </div>
      ) : null}

      {isLiveDistanceMeasure ? (
        <FarmMapFloatingPanel
          panelId="measure-distance"
          farmId={farmId}
          title={t("Medir distância")}
          defaultLayout={MEASURE_DISTANCE_PANEL_LAYOUT}
        >
          <p className="farm-map-measure-distance__hint">
            {t(
              "Clique no mapa para marcar os pontos. A distância é somada automaticamente."
            )}
          </p>
          <div className="farm-map-measure-distance__value">
            <span>{t("Distância total")}</span>
            <strong>{formatDistanceM(draftLineDistanceM)}</strong>
          </div>
          {draftPointCount >= 2 ? (
            <div className="farm-map-measure-distance__segment">
              <span>{t("Último trecho")}</span>
              <strong>{formatDistanceM(draftLastSegmentM)}</strong>
            </div>
          ) : null}
          <div className="farm-map-measure-distance__meta">
            {t("Pontos")}: {draftPointCount}
          </div>
          <div className="farm-map-drawing-instruction__actions">
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={onUndoDraftPoint}
              disabled={draftPointCount === 0}
            >
              {t("Desfazer ponto")}
            </button>
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={onClearMeasureDraft}
              disabled={draftPointCount === 0}
            >
              {t("Limpar medição")}
            </button>
          </div>
        </FarmMapFloatingPanel>
      ) : null}

      {showInstruction ? (
        <FarmMapFloatingPanel
          panelId="instruction"
          farmId={farmId}
          title={
            isAddingHole
              ? t("Recorte interno")
              : isEditingSubFarm
                ? t("Ajustar perímetro da fazenda")
              : isEditingCrop
                ? t("Ajustar talhão no mapa")
                : activeTool === "farm"
                  ? t("Desenhar fazenda")
                  : t("Desenhar no mapa")
          }
          defaultLayout={INSTRUCTION_PANEL_LAYOUT}
        >
          <p>{instructionText()}</p>
          <div className="farm-map-drawing-instruction__actions">
            <span>
              {t("Pontos")}: {draftPointCount}
            </span>
            {isAddingHole ? (
              <button
                type="button"
                className="btn btn-default btn-sm"
                onClick={onCancelAddingHole}
              >
                {t("Cancelar recorte")}
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={onUndoDraftPoint}
              disabled={draftPointCount === 0}
            >
              {t("Desfazer ponto")}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={onFinishDraft}
              disabled={draftPointCount < minDraftPoints}
            >
              {isAddingHole ? t("Concluir recorte") : t("Concluir")}
            </button>
            {isAddingHole ? (
              <span className="farm-map-drawing-instruction__hint">
                {t("Depois clique em Salvar talhão para persistir.")}
              </span>
            ) : null}
          </div>
        </FarmMapFloatingPanel>
      ) : null}

      {pendingShape && !isAddingHole ? (
        <FarmMapFloatingPanel
          panelId={
            pendingShape.type === "farm" ? "result-farm" : "result"
          }
          farmId={farmId}
          title={
            pendingShape.type === "farm"
              ? isUpdatingFarm
                ? t("Atualizar perímetro da fazenda")
                : t("Perímetro da fazenda")
              : isUpdatingCrop
                ? t("Atualizar talhão")
                : t("Medição do talhão")
          }
          defaultLayout={
            pendingShape.type === "farm"
              ? FARM_RESULT_PANEL_LAYOUT
              : RESULT_PANEL_LAYOUT
          }
        >
          {pendingShape.type === "farm" ? (
            <>
              <div>
                <strong>{t("Perímetro")}:</strong>{" "}
                {formatDistanceM(pendingShape.perimeterM)}
              </div>
              <div>
                <strong>{t("Talhões dentro")}:</strong>{" "}
                {pendingShape.assignedPlotCount || 0}
              </div>
              <p className="farm-map-drawing-result__hint">
                {t(
                  "Somente o contorno será salvo. Talhões detectados dentro do perímetro serão vinculados à fazenda."
                )}
              </p>
            </>
          ) : (
            <>
              <div>
                <strong>{t("Área líquida")}:</strong>{" "}
                {formatAreaHa(pendingShape.areaHa)}
              </div>
              <div>
                <strong>{t("Perímetro")}:</strong>{" "}
                {formatDistanceM(pendingShape.perimeterM)}
              </div>
              {holeCount > 0 ? (
                <p className="farm-map-drawing-result__hint">
                  {t(
                    "A área líquida acima já desconta os recortes. Clique em Atualizar talhão para gravar."
                  )}
                </p>
              ) : null}
              {holeCount > 0 ? (
                <div className="farm-map-drawing-holes">
                  <strong>{t("Recortes internos")}:</strong> {holeCount}
                  <ul>
                    {(pendingShape.holes || []).map((hole, index) => (
                      <li key={`hole-${index}`}>
                        {t("Recorte")} {index + 1}
                        <button
                          type="button"
                          className="btn btn-link btn-sm"
                          onClick={() => onRemoveHole(index)}
                        >
                          {t("Remover")}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          )}

          <div className="farm-map-drawing-result__actions">
            {pendingShape.type === "polygon" ? (
              <button
                type="button"
                className="btn btn-default btn-sm"
                onClick={onStartAddingHole}
                disabled={savingDrawing}
              >
                <FaCut style={{ marginRight: 6 }} />
                {t("Adicionar recorte interno")}
              </button>
            ) : null}

            {!measureOnly && pendingShape.type === "polygon" ? (
              <>
                <input
                  type="text"
                  className="form-control input-sm"
                  placeholder={t("Nome do talhão (ex: T)")}
                  value={cropName}
                  onChange={(event) => setCropName(event.target.value)}
                />
                {cropName.trim() ? (
                  <p className="farm-map-drawing-result__preview">
                    {isUpdatingCrop ? t("Nome atualizado") : t("Será salvo como")}:{" "}
                    <strong>{plotPreviewName}</strong>
                  </p>
                ) : (
                  <p className="farm-map-drawing-result__preview">
                    {isUpdatingCrop ? t("Nome atualizado") : t("Será salvo como")}:{" "}
                    <strong>{buildPlotName("", pendingShape.areaHa)}</strong>
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={savingDrawing || !canSaveCrop}
                  onClick={handleSaveCropClick}
                >
                  {savingDrawing
                    ? t("Salvando...")
                    : isUpdatingCrop
                      ? t("Atualizar talhão")
                      : t("Salvar talhão")}
                </button>
              </>
            ) : null}

            {pendingShape.type === "farm" ? (
              <>
                {!isUpdatingFarm ? (
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder={t("Nome da fazenda (ex.: Fazenda San Rafael)")}
                    value={farmName}
                    onChange={(event) => setFarmName(event.target.value)}
                  />
                ) : (
                  <p className="farm-map-drawing-result__preview">
                    {t("Fazenda")}: <strong>{farmName || pendingShape.subFarmName}</strong>
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={
                    savingDrawing ||
                    (!isUpdatingFarm && !farmName.trim())
                  }
                  onClick={handleSaveFarmClick}
                >
                  {savingDrawing
                    ? t("Salvando...")
                    : isUpdatingFarm
                      ? t("Atualizar perímetro e vincular talhões")
                      : t("Salvar fazenda e vincular talhões")}
                </button>
              </>
            ) : null}

            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={onClearPending}
            >
              {t("Descartar")}
            </button>
          </div>
        </FarmMapFloatingPanel>
      ) : null}
    </>
  );
}

export default FarmMapDrawingPanel;
